import React, { useState } from 'react';
import developerTitle from '../../assets/img/developer-title.svg';
import { useDispatch, useSelector } from 'react-redux';
import './changePassword.scss';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams } from 'react-router';
import { passwordChange } from '../../layout/redux/layoutActions';
import { IconVisible } from '../../component/icon/visible';
import { IconInvisible } from '../../component/icon/invisible';

type IsVisiblePasswords = {
  password: boolean;
  passwordConfirmation: boolean;
};

export const ChangePassword = (): JSX.Element => {
  const { preference } = useSelector((state: any) => state.layoutState);
  const [isVisible, setIsVisible] = useState<IsVisiblePasswords>({
    password: false,
    passwordConfirmation: false,
  });
  const nls = require(`./nls/${preference.language.id}.json`);
  const dispatch = useDispatch();
  const registerFormSchema = z
    .object({
      password: z.string().min(8, {
        message: nls.changePassword.error.atLeastEightCharacters,
      }),
      passwordConfirmation: z.string().min(1, {
        message: nls.changePassword.error.notEmpty,
      }),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      message: nls.changePassword.error.passwordMustBeConfirmed,
      path: ['passwordConfirmation'],
    });

  type ChangePasswordFormData = z.infer<typeof registerFormSchema>;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(registerFormSchema),
  });
  const params = useParams();

  const onSubmit = (passwordObj: ChangePasswordFormData): void => {
    const payload = {
      new_password: passwordObj.password,
      password_confirmation: passwordObj.passwordConfirmation,
    };
    dispatch(passwordChange(payload, params['id' as keyof typeof params]));
  };
  return (
    <div className="change-password-container">
      <div className="change-password-form-container">
        <img src={developerTitle} alt="developer" />
        <h6>{nls.changePassword.title}</h6>
        <p>{nls.changePassword.description}</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="input-group-container">
            <label>{nls.changePassword.newPassword}</label>
            <input
              type={isVisible.password ? 'text' : 'password'}
              {...register('password')}
            />
            {isVisible.password ? (
              <button
                type="button"
                className="visibility-icon-btn"
                onClick={() => {
                  setIsVisible({ ...isVisible, password: false });
                }}
              >
                <IconVisible cy="changePassword" />
              </button>
            ) : (
              <button
                type="button"
                className="visibility-icon-btn"
                onClick={() => {
                  setIsVisible({ ...isVisible, password: true });
                }}
              >
                <IconInvisible cy="changePassword" />
              </button>
            )}
            {errors.password && <p>{errors.password.message}</p>}
          </div>
          <div className="input-group-container">
            <label>{nls.changePassword.newPasswordConfirmation}</label>
            <input
              type={isVisible.passwordConfirmation ? 'text' : 'password'}
              {...register('passwordConfirmation')}
            />
            {isVisible.passwordConfirmation ? (
              <button
                className="visibility-icon-btn"
                onClick={() => {
                  setIsVisible({ ...isVisible, passwordConfirmation: false });
                }}
                type="button"
              >
                <IconVisible cy="changePasswordConfirmation" />
              </button>
            ) : (
              <button
                className="visibility-icon-btn"
                onClick={() => {
                  setIsVisible({ ...isVisible, passwordConfirmation: true });
                }}
                type="button"
              >
                <IconInvisible cy="changePasswordConfirmation" />
              </button>
            )}
            {errors.passwordConfirmation && (
              <p>{errors.passwordConfirmation.message}</p>
            )}
          </div>
          <input
            type="submit"
            className="submit-button"
            value={nls.changePassword.button}
            disabled={isSubmitting}
          />
        </form>
      </div>
      <small>{nls.copyright}</small>
    </div>
  );
};
